import React from 'react';
import {
  Typography,
  TextField,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Box,
  Paper,
} from '@mui/material';
import { TWButton } from '../../../shared/components';
import { FormData } from '../types/forms';

interface FormSectionProps {
  formData: FormData;
  isProcessing: boolean;
  onInputChange: (field: keyof FormData) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCheckboxChange: (value: string) => (event: React.ChangeEvent<HTMLInputElement>) => void;
  onProcessQuestions: () => void;
  onReset: () => void;
  isQuestionsReadOnly?: boolean;
  disableSubmit?: boolean;
}

const commonTextFieldStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1,
    backgroundColor: 'white',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#48a1ad',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#48a1ad',
      borderWidth: 2,
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#48a1ad',
    fontWeight: 500,
  }
};

const formLabelStyles = {
  color: '#333',
  fontWeight: 500,
  mb: 1,
  fontSize: '0.9rem'
};

const radioStyles = {
  color: '#48a1ad',
  '&.Mui-checked': { color: '#48a1ad' }
};

const checkboxStyles = {
  color: '#48a1ad',
  '&.Mui-checked': { color: '#48a1ad' }
};

const formControlLabelStyles = {
  '& .MuiFormControlLabel-label': { 
    fontSize: '0.9rem',
    fontWeight: 500
  }
};



const FormSection: React.FC<FormSectionProps> = ({
  formData,
  isProcessing,
  onInputChange,
  onCheckboxChange,
  onProcessQuestions,
  isQuestionsReadOnly = false,
  onReset,
  disableSubmit = false,
}) => {
  return (
    <Box>
      {/* Client Opportunity Section */}
      <Paper sx={{ 
        p: 3, 
        mb: 3, 
        borderRadius: 2,
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="h6" sx={{ 
          color: '#333', 
          fontWeight: 600, 
          mb: 3,
          fontSize: '1.2rem'
        }}>
          Client opportunity
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Row 1: Client name and Country/Office */}
          <Box sx={{ display: 'flex', gap: 3 }}>
            <TextField
              label="Client name"
              value={formData.clientName}
              onChange={onInputChange('clientName')}
              required
              placeholder="e.g. XXX Bank"
              sx={{ flex: 1, ...commonTextFieldStyles }}
            />
            <TextField
              label="Country and/or Thoughtworks office"
              value={formData.countryOffice}
              onChange={onInputChange('countryOffice')}
              placeholder="e.g. Sales in Australia, delivery in China"
              sx={{ flex: 1, ...commonTextFieldStyles }}
            />
          </Box>
          
          {/* Row 2: Engagement type and Laptop type */}
          <Box sx={{ display: 'flex', gap: 3 }}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel sx={formLabelStyles}>Engagement type</FormLabel>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.engagementType.includes('delivery')}
                      onChange={onCheckboxChange('delivery')}
                      size="small"
                      sx={checkboxStyles}
                    />
                  }
                  label="Delivery"
                  sx={formControlLabelStyles}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.engagementType.includes('advisory')}
                      onChange={onCheckboxChange('advisory')}
                      size="small"
                      sx={checkboxStyles}
                    />
                  }
                  label="Advisory"
                  sx={formControlLabelStyles}
                />
              </FormGroup>
            </FormControl>
            
            <FormControl sx={{ flex: 1 }}>
              <FormLabel sx={formLabelStyles}>Laptop type</FormLabel>
              <RadioGroup
                value={formData.laptopType}
                onChange={onInputChange('laptopType')}
                row
              >
                <FormControlLabel 
                  value="tw-laptop" 
                  control={<Radio size="small" sx={radioStyles} />} 
                  label="Thoughtworks"
                  sx={formControlLabelStyles}
                />
                <FormControlLabel 
                  value="client-laptop" 
                  control={<Radio size="small" sx={radioStyles} />} 
                  label="Client provided"
                  sx={formControlLabelStyles}
                />
              </RadioGroup>
            </FormControl>
          </Box>
          
          {/* Row 3: Industry segment and Contract type */}
          <Box sx={{ display: 'flex', gap: 3 }}>
            <TextField
              label="Industry segment"
              value={formData.market}
              onChange={onInputChange('market')}
              placeholder="e.g. BFSI"
              sx={{ flex: 1, ...commonTextFieldStyles }}
            />
            <FormControl sx={{ flex: 1 }}>
              <FormLabel sx={formLabelStyles}>Contract type</FormLabel>
              <RadioGroup
                value={formData.contractType}
                onChange={onInputChange('contractType')}
                row
              >
                <FormControlLabel 
                  value="new" 
                  control={<Radio size="small" sx={radioStyles} />} 
                  label="New contract"
                  sx={formControlLabelStyles}
                />
                <FormControlLabel 
                  value="extension" 
                  control={<Radio size="small" sx={radioStyles} />} 
                  label="Extension"
                  sx={formControlLabelStyles}
                />
              </RadioGroup>
            </FormControl>
          </Box>
          
          {/* Row 4: Regulatory constraints */}
          <TextField
            fullWidth
            label="Regulatory constraints"
            value={formData.regulatoryConstraints}
            onChange={onInputChange('regulatoryConstraints')}
            multiline
            rows={2}
            placeholder="e.g. PCI-DSS compliance requirements..."
            sx={commonTextFieldStyles}
          />
        </Box>
      </Paper>

      {/* Security Questions Section */}
      <Paper sx={{ 
        p: 3, 
        mb: 3, 
        borderRadius: 2,
        backgroundColor: 'white',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Typography variant="h6" sx={{ 
          color: '#333', 
          fontWeight: 600, 
          mb: 3,
          fontSize: '1.2rem'
        }}>
          Security questions
        </Typography>
        
        <TextField
          fullWidth
          label={isQuestionsReadOnly ? "Security questions (from uploaded document)" : "Enter one or more security questions"}
          value={formData.securityQuestions}
          onChange={onInputChange('securityQuestions')}
          multiline
          rows={6}
          required
          placeholder="Enter one or more security questions..."
          disabled={isQuestionsReadOnly}
          sx={{
            ...commonTextFieldStyles,
            ...(isQuestionsReadOnly && {
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f5f5f5',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e0e0',
                },
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#333',
                cursor: 'not-allowed',
              },
            }),
          }}
        />
        
        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, mt: 3, justifyContent: 'flex-start' }}>
          <TWButton
            buttonType="primary"
            onClick={onProcessQuestions}
            loading={isProcessing}
            disabled={disableSubmit || !formData.clientName.trim() || !formData.securityQuestions.trim()}
            label={isProcessing ? 'Processing...' : 'Get answers'}
            sx={{ 
              fontSize: '1rem', 
              fontWeight: 500, 
              minWidth: 180,
              minHeight: '48px',
              backgroundColor: '#48a1ad',
              borderRadius: 0,
              '&:hover': {
                backgroundColor: '#3a8a94',
              },
            }}
          />
          <TWButton
            buttonType="secondary"
            onClick={onReset}
            disabled={isProcessing}
            label="Reset"
            sx={{ 
              fontSize: '1rem', 
              fontWeight: 500, 
              minWidth: 150,
              minHeight: '48px',
              justifyContent: 'center',
              border: '1.5px solid #003D4F',
              color: '#003D4F',
              borderRadius: 0,
              '&:hover': {
                backgroundColor: '#f5f5f5',
                border: '1.5px solid #003D4F',
              },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default FormSection;
